import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { confirmDiscard } from "./unsaved.ts";

/** Small presentational pieces shared across screens. */

export function Kpi({
  label,
  value,
  unit,
  note,
  tone,
}: {
  label: string;
  value: string | number;
  unit?: string | undefined;
  note?: string | undefined;
  tone?: "good" | "bad" | undefined;
}) {
  return (
    <div className="kpi">
      <div className="label-mono">{label}</div>
      <div className="kpi-value">
        {value}
        {unit && <span className="kpi-unit">{unit}</span>}
      </div>
      {note && <div className={`kpi-note${tone ? ` ${tone}` : ""}`}>{note}</div>}
    </div>
  );
}

export function BrandBadge({ badge, color }: { badge: string; color: string }) {
  return (
    <span className="badge" style={{ background: color }}>
      {badge}
    </span>
  );
}

const STATUS_TONE: Record<string, string> = {
  sent: "ok",
  sending: "info",
  scheduled: "warn",
  draft: "mute",
  paused: "warn",
  cancelled: "bad",
};

export function StatusPill({ status }: { status: string }) {
  return <span className={`pill ${STATUS_TONE[status] ?? "mute"}`}>{status}</span>;
}

export function Card({
  title,
  subtitle,
  action,
  children,
}: {
  title?: string | undefined;
  subtitle?: string | undefined;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card">
      {(title || action) && (
        <div className="card-head">
          <div>
            {title && <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/**
 * Open and click figures come with a caveat everywhere they appear: privacy
 * proxies and link scanners are excluded, but no heuristic catches all of them.
 * Saying so beside the number is better than letting someone plan around it.
 */
export function EngagementCaveat() {
  return (
    <p className="caveat">
      Opens and clicks exclude detected privacy proxies and link scanners. They still under-report
      readers who block images, so treat replies as the reliable signal.
    </p>
  );
}

/**
 * A panel that slides in from the right over the page, for working on one
 * record without losing your place in the list behind it. Esc, the backdrop
 * and the close button all dismiss it; focus moves into it on open and back
 * to whatever opened it on close.
 */
export function Drawer({
  title,
  subtitle,
  onClose,
  actions,
  wide,
  children,
}: {
  title: ReactNode;
  /** For editors that need the room, e.g. a sequence's steps. */
  wide?: boolean | undefined;
  subtitle?: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // The latest onClose, read through a ref: callers pass inline functions,
  // and an effect keyed on them re-ran on every parent render — moving focus
  // back to the panel and out of whatever field was being typed in.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Closing with unsaved edits in the panel asks first, like leaving a page.
  const close = useCallback(() => {
    void confirmDiscard().then((ok) => ok && onCloseRef.current());
  }, []);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [close]);

  return (
    <div className="drawer-root">
      <button
        type="button"
        className="drawer-backdrop"
        aria-label="Close panel"
        tabIndex={-1}
        onClick={close}
      />
      <div
        className={`drawer${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        ref={panel}
      >
        <div className="drawer-head">
          <div className="drawer-title">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions}
          <button type="button" className="drawer-close" aria-label="Close" onClick={close}>
            ×
          </button>
        </div>
        <div className="drawer-body">{children}</div>
      </div>
    </div>
  );
}

/**
 * Placeholder rows while a table's data loads. Without them a table renders
 * empty first, which reads as "you have nothing" on a slow connection.
 */
export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <table aria-busy="true" aria-label="Loading">
      <tbody>
        {Array.from({ length: rows }, (_, r) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders that never reorder.
          <tr key={r}>
            {Array.from({ length: cols }, (_, c) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: as above.
              <td key={c}>
                <span className="skeleton" style={{ width: c === 0 ? "70%" : "45%" }} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** `value`, once it has stopped changing for `ms` — for search-as-you-type. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/** Placeholders while loading, the empty message when there is nothing, else the table. */
export function ListBody({
  loading,
  empty,
  emptyText,
  cols,
  children,
}: {
  loading: boolean;
  empty: boolean;
  emptyText: ReactNode;
  cols?: number;
  children: ReactNode;
}) {
  if (loading) return <TableSkeleton cols={cols ?? 4} />;
  if (empty) return <Empty>{emptyText}</Empty>;
  return <>{children}</>;
}
